import { type ReactNode, useEffect, useState } from 'react'
import cx from 'classnames'
import { AnimatePresence, usePresence } from 'motion/react'
import styles from './AlertsHost.module.css'
import { match } from '@/utils/pattern-match-util'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { Spinner } from '@/components/Spinner'

const ANIMATION_DURATION = 500

export interface AlertsHostProps {
  alerts: readonly { id: string; content: ReactNode }[]
}

export function AlertsHost({ alerts }: AlertsHostProps) {
  return (
    <div className={styles.root}>
      <AnimatePresence>
        {alerts.map((alert) => (
          <AlertTransition key={alert.id}>{alert.content}</AlertTransition>
        ))}
      </AnimatePresence>
    </div>
  )
}

interface AlertTransitionProps {
  children: ReactNode
}

/**
 * Plays the stylesheet's enter animation on mount, and its leave animation
 * before `AnimatePresence` removes the alert.
 */
function AlertTransition({ children }: AlertTransitionProps) {
  const [isPresent, safeToRemove] = usePresence()
  const [isEntering, setIsEntering] = useState(true)

  useEffect(() => {
    const timeout = setTimeout(() => setIsEntering(false), ANIMATION_DURATION)
    return () => clearTimeout(timeout)
  }, [])

  useEffect(() => {
    if (isPresent) {
      return
    }

    const timeout = setTimeout(safeToRemove, ANIMATION_DURATION)
    return () => clearTimeout(timeout)
  }, [isPresent, safeToRemove])

  return (
    <div
      className={cx(
        isPresent && isEntering && styles.animationEnter,
        !isPresent && styles.animationLeave,
      )}
    >
      {children}
    </div>
  )
}

export type AlertViewState =
  'neutral' | 'pending' | 'success' | 'warning' | 'error'

export interface AlertViewProps {
  title: string
  description: string
  state: AlertViewState
  actions: readonly { title: string; onPress: () => unknown }[]
  onInteract: () => unknown
  onMouseEnter: () => unknown
  onDismiss: () => unknown
}

export function AlertView({
  title,
  description,
  state,
  actions,
  onInteract,
  onMouseEnter,
  onDismiss,
}: AlertViewProps) {
  return (
    <div className={styles.alert}>
      <div
        onMouseEnter={onMouseEnter}
        data-test={`alert-${title}`}
        className={cx(
          styles.box,
          styles[state],
          // On macOS, when there are no actions on Notifications, the "close" button
          // reveals itself on hover.
          actions.length === 0 && styles.hideActionsUntilHover,
        )}
      >
        <RawButton className={styles.main} onPress={onInteract}>
          <div className={styles.iconContainer}>
            {match(state, {
              neutral: () => <Icon auto name="info" />,
              pending: () => <Spinner color="#000" size={20} />,
              success: () => <Icon auto name="tick" />,
              warning: () => <Icon auto name="info" rotate={180} />,
              error: () => <Icon auto name="info" rotate={180} />,
            })}
          </div>
          <div className={styles.content}>
            {title && <p className={styles.title}>{title}</p>}
            {description && (
              <p title={description} className={styles.description}>
                {description}
              </p>
            )}
          </div>
        </RawButton>
        <div className={styles.actions}>
          {actions.length === 0 ? (
            <AlertAction
              title="Close"
              onPress={onDismiss}
              testId={`alert-${title}-button-Close`}
            >
              Close
            </AlertAction>
          ) : (
            actions.map((action, idx) => (
              <AlertAction
                key={idx}
                title={action.title}
                onPress={action.onPress}
                testId={`alert-${title}-button-${action.title}`}
              >
                {action.title}
              </AlertAction>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

interface AlertActionProps {
  onPress(): unknown
  children: ReactNode
  title: string
  testId?: string
}

function AlertAction({ onPress, children, title, testId }: AlertActionProps) {
  return (
    <RawButton
      className={styles.action}
      title={title}
      testId={testId}
      onPress={onPress}
    >
      <span className={styles.actionText}>{children}</span>
    </RawButton>
  )
}
